import type { ApiError, BasePagingResponse, BaseResponse } from "../../../lib/api-contract";
import api from "../../../lib/axios";
import { ApiResultError } from "../../../lib/result-codes";
import { getOrganizations } from "../../organizations/api/organizations.api";
import type { Organization } from "../../organizations/types/organization.types";
import {
  CampaignDashboardResponse,
  CampaignHierarchyItem,
  CampaignHierarchyResponse,
  CampaignImportResponse,
  CampaignImportValidationError,
  CreateCampaignRequest,
  CreateCampaignResponse,
  CreateOpportunityRequest,
  CreateOpportunityResponse,
  CreateProgramRequest,
  CreateProgramResponse,
  OpportunityHierarchyItem,
  ProgramHierarchyItem,
  ProgramListResponse,
  UpdateOpportunityRequest,
  UpdateOpportunityResponse,
  UpdateProgramRequest,
  UpdateProgramResponse,
  normalizeCampaignHierarchyResponse,
  normalizeCreateOpportunityPayload,
  normalizeProgramListItem,
} from "../types";
import { CAMPAIGN_STATUSES } from "../types/campaign-status.constants";

interface CampaignSourceResponse {
  id: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}
export interface CampaignSourceListRequest {
  search?: string;
  page?: number;
  size?: number;
  opportunityId?: string;
}
export interface DemoEventCampaignInfo {
  campaignId: string;
  campaignName: string;
  programId: string;
  programName: string;
  opportunityId: string;
  opportunityName: string;
}

export class CampaignServiceError extends Error {
  readonly errors: ApiError[];
  constructor(message: string, errors: ApiError[] = []) {
    super(message);
    this.name = "CampaignServiceError";
    this.errors = errors;
  }
}

export class CampaignImportServiceError extends CampaignServiceError {
  readonly importErrors: CampaignImportValidationError[];
  constructor(message: string, errors: ApiError[], importErrors: CampaignImportValidationError[]) {
    super(message, errors);
    this.name = "CampaignImportServiceError";
    this.importErrors = importErrors;
  }
}

function errorResponse(error: unknown): BaseResponse<unknown> | undefined {
  if (error instanceof ApiResultError) return error.response;
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const response = error.response;
  if (typeof response !== "object" || response === null || !("data" in response)) return undefined;
  const data = response.data;
  return typeof data === "object" && data !== null && "errors" in data
    ? (data as BaseResponse<unknown>)
    : undefined;
}

function campaignError(error: unknown, fallback: string): CampaignServiceError {
  if (error instanceof CampaignServiceError) return error;
  const response = errorResponse(error);
  return new CampaignServiceError(
    response?.user_message ||
      response?.message ||
      (error instanceof Error ? error.message : fallback),
    response?.errors ?? [],
  );
}

function unwrap<T>(response: BaseResponse<T>, fallback: string): T {
  if (!response.success || response.data === null)
    throw new CampaignServiceError(
      response.user_message || response.message || fallback,
      response.errors,
    );
  return response.data;
}

function normalizeOpportunity<T extends OpportunityHierarchyItem>(opportunity: T): T {
  return normalizeCampaignHierarchyResponse({
    campaigns: [
      {
        id: "campaign",
        name: "campaign",
        status: CAMPAIGN_STATUSES.Unactive,
        programCount: 1,
        opportunityCount: 1,
        programs: [
          {
            id: "program",
            campaignId: "campaign",
            name: "program",
            status: CAMPAIGN_STATUSES.Unactive,
            opportunityCount: 1,
            opportunities: [opportunity],
          },
        ],
      },
    ],
  }).campaigns[0].programs[0].opportunities[0] as T;
}

export function getDemoEventCampaignMap(): Record<string, DemoEventCampaignInfo> {
  return {};
}

async function getData<T>(url: string, fallback: string, params?: object): Promise<T> {
  try {
    return unwrap((await api.get<BaseResponse<T>>(url, { params })).data, fallback);
  } catch (error) {
    throw campaignError(error, fallback);
  }
}

async function sendData<T>(
  method: "post" | "put",
  url: string,
  body: unknown,
  fallback: string,
): Promise<T> {
  try {
    return unwrap((await api[method]<BaseResponse<T>>(url, body)).data, fallback);
  } catch (error) {
    throw campaignError(error, fallback);
  }
}

export async function getCampaignHierarchy(params?: {
  searchTerm?: string;
  statusFilter?: CAMPAIGN_STATUSES | "all";
}): Promise<CampaignHierarchyResponse> {
  return normalizeCampaignHierarchyResponse(
    await getData("/v1/portal/campaigns", "Failed to load campaign hierarchy.", {
      search: params?.searchTerm?.trim() || undefined,
      status: params?.statusFilter === "all" ? undefined : params?.statusFilter,
    }),
  );
}

export function getCampaignDashboard(params?: {
  searchTerm?: string;
  statusFilter?: CAMPAIGN_STATUSES | "all";
}): Promise<CampaignDashboardResponse> {
  return getData("/v1/portal/campaigns/statistics", "Failed to load campaign dashboard.", {
    search: params?.searchTerm?.trim() || undefined,
    status: params?.statusFilter === "all" ? undefined : params?.statusFilter,
  });
}

export function createCampaign(payload: CreateCampaignRequest): Promise<CreateCampaignResponse> {
  return sendData("post", "/v1/portal/campaigns", payload, "Failed to create campaign.");
}
export function createProgram(
  campaignId: string,
  payload: Omit<CreateProgramRequest, "campaignId">,
): Promise<CreateProgramResponse> {
  return sendData(
    "post",
    `/v1/portal/campaigns/${campaignId}/programs`,
    payload,
    "Failed to create program.",
  );
}

export async function listCampaignPrograms(campaignId: string): Promise<ProgramListResponse> {
  return {
    programs: (
      await getData<ProgramHierarchyItem[]>(
        `/v1/portal/campaigns/${campaignId}/programs`,
        "Failed to load campaign programs.",
      )
    ).map(normalizeProgramListItem),
  };
}

export function updateProgram(
  programId: string,
  payload: UpdateProgramRequest,
): Promise<UpdateProgramResponse> {
  return sendData(
    "put",
    `/v1/portal/campaign-programs/${programId}`,
    payload,
    "Failed to update program.",
  );
}

export async function updateCampaign(
  campaignId: string,
  payload: Partial<CreateCampaignRequest>,
): Promise<CampaignHierarchyItem> {
  return normalizeCampaignHierarchyResponse({
    campaigns: [
      await sendData(
        "put",
        `/v1/portal/campaigns/${campaignId}`,
        payload,
        "Failed to update campaign.",
      ),
    ],
  }).campaigns[0];
}

export async function createOpportunity(
  programId: string,
  payload: Omit<CreateOpportunityRequest, "programId">,
): Promise<CreateOpportunityResponse> {
  return normalizeOpportunity(
    await sendData(
      "post",
      `/v1/portal/campaign-programs/${programId}/opportunities`,
      normalizeCreateOpportunityPayload(payload),
      "Failed to create opportunity.",
    ),
  );
}

export async function updateOpportunity(
  _programId: string,
  opportunityId: string,
  payload: UpdateOpportunityRequest,
): Promise<UpdateOpportunityResponse> {
  return normalizeOpportunity(
    await sendData(
      "put",
      `/v1/portal/campaign-opportunities/${opportunityId}`,
      normalizeCreateOpportunityPayload(payload),
      "Failed to update opportunity.",
    ),
  );
}

export function listEventsWithoutCampaign(
  request: CampaignSourceListRequest = {},
): Promise<BasePagingResponse<CampaignSourceResponse>> {
  return getData(
    "/v1/portal/campaign-opportunities/event-sources",
    "Failed to load event sources.",
    request,
  );
}
export function listSurveySources(
  request: CampaignSourceListRequest = {},
): Promise<BasePagingResponse<CampaignSourceResponse>> {
  return getData(
    "/v1/portal/campaign-opportunities/survey-sources",
    "Failed to load survey sources.",
    request,
  );
}

export async function listCampaignImportOrganizations(): Promise<Organization[]> {
  try {
    return (await getOrganizations({ index: 0, size: 100 })).list;
  } catch (error) {
    throw campaignError(error, "Failed to load organizations.");
  }
}

export async function importCampaigns(
  file: File,
  organizationId?: string,
): Promise<CampaignImportResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (organizationId) formData.append("organizationId", organizationId);
  try {
    return unwrap(
      (
        await api.post<BaseResponse<CampaignImportResponse>>(
          "/v1/portal/campaigns/import",
          formData,
          { headers: { "Content-Type": false } },
        )
      ).data,
      "Campaign import failed.",
    );
  } catch (error) {
    const serviceError = campaignError(error, "Campaign import failed.");
    const importErrors = serviceError.errors
      .filter((item) => Boolean(item.sheet || item.row || item.field))
      .map((item) => ({
        sheet: item.sheet ?? "",
        row: item.row ?? undefined,
        field: item.field ?? undefined,
        message: item.message,
      }));
    throw new CampaignImportServiceError(serviceError.message, serviceError.errors, importErrors);
  }
}

export async function deleteCampaign(campaignId: string): Promise<void> {
  try {
    await api.delete(`/v1/portal/campaigns/${campaignId}`);
  } catch (error) {
    throw campaignError(error, "Failed to delete campaign.");
  }
}
